import React, { useMemo, useState } from 'react'
import {
  DesignedModal,
  Button,
  RadioButtonGroup,
  StyledTextArea,
  useFrontEndEventTrackingService,
  Toast,
} from '@clickcease/cc-client-components-lib'
import { useZendeskChat } from 'src/hooks/zendesk/useZendeskChat'
import { Trans, useTranslation } from 'react-i18next'
import { cancelReasons, PRODUCTS } from './helper'
import {
  ModalBody,
  Label,
  ModalDescription,
  ContactSupport,
  StyledLink,
  RequiredText,
  BoldText,
  styleReset,
  ToastWrapper,
} from './CancelModal.styled'
import { CancelPlanRequestProps, CancelationReason } from 'Features/accountSettings/api/cancelPlan'
import { CancelProductPart } from './components/CancelProductPart'
import { ProductTypes, ProductTypesWithoutBundle } from 'Types/ProductTypes'
import { useIsProductTrial } from 'Hooks/trial/useIsProductTrial'
import { useExtendTrial } from 'Features/accountSettings/api/extendTrial'
import { useGetPlansDetails } from 'Features/accountSettings/api/getPlansDetails'
import { getProductName } from 'Utils/productName'

type CancelPlanModalProps = {
  isOpen: boolean
  setIsOpen: (val: boolean) => void
  onConfirm: (from: CancelPlanRequestProps) => void
  loading?: boolean
  cancelProduct?: boolean
  product?: ProductTypesWithoutBundle
  setProduct?: (product: ProductTypesWithoutBundle) => void
}

export const CancelPlanModal = ({
  isOpen,
  setIsOpen,
  onConfirm,
  loading,
  cancelProduct,
  product,
  setProduct,
}: CancelPlanModalProps) => {
  const { t } = useTranslation('accountSettings')
  const { openChat } = useZendeskChat()
  const { track } = useFrontEndEventTrackingService()
  const extendTrial = useExtendTrial()
  const { isExtendTrialAllowed, isProductInTrial, productsInTrial } = useIsProductTrial()
  const [formError, setFormError] = useState(false)
  const [form, setForm] = useState({
    reason: '',
    description: '',
  })
  const { data: plansDetails } = useGetPlansDetails()
  const isPlanCanceled = !!plansDetails?.cancelled

  const productsToBeCancelled = useMemo(() => {
    if (!plansDetails || !plansDetails.productCancelled) return []
    return Object.keys(plansDetails.productCancelled)
      .filter((key) => plansDetails.productCancelled[key] === true)
      .map((key) => getProductName(key as ProductTypes))
  }, [plansDetails])

  const translate = (key: string) => t(`plan_details.reason_modal.${key}`)

  const canProductExtendTrial = cancelProduct && isExtendTrialAllowed(product)

  const products = PRODUCTS.map((product) => ({
    ...product,
    disabled: !!productsInTrial && !isProductInTrial(product.value),
  }))

  const handleSubmit = () => {
    const { reason, description } = form
    if (!reason || !description || (cancelProduct && !product)) setFormError(true)
    else {
      setFormError(false)
      track(
        ['segment'],
        cancelProduct ? `Account canceled` : `Product canceled ${product ?? ''}`,
        {}
      )
      onConfirm({ reason: reason as CancelationReason, description })
    }
  }

  const handleCancel = () =>
    canProductExtendTrial
      ? extendTrial.mutate(undefined, { onSuccess: () => setIsOpen(false) })
      : setIsOpen(false)

  return (
    <DesignedModal
      id={'cancel-plan-modal'}
      isOpen={isOpen}
      title={translate('title')}
      onOpenChange={setIsOpen}
      style={{ width: '600px' }}
      actions={
        <>
          {cancelProduct && (
            <Button
              variant="ghost"
              loading={extendTrial.isLoading}
              text={translate(canProductExtendTrial ? 'extend_trial' : 'keep_me_protected')}
              onClick={handleCancel}
            />
          )}
          <Button
            text={translate(cancelProduct ? 'cancel_product' : 'modal_confirm_button')}
            loading={loading}
            onClick={handleSubmit}
            disabled={!!productsToBeCancelled?.length || isPlanCanceled}
          />
        </>
      }
    >
      {!cancelProduct && !!productsToBeCancelled?.length && (
        <ToastWrapper>
          <Toast type="warning">
            <Trans
              ns={'general'}
              i18nKey={'errors.CANCEL_SUBSCRIPTION_FAILED'}
              values={{
                product: productsToBeCancelled,
              }}
            />
          </Toast>
        </ToastWrapper>
      )}

      {cancelProduct && isPlanCanceled && (
        <ToastWrapper>
          <Toast type="warning">
            <Trans ns={'general'} i18nKey={'errors.CANCEL_PRODUCT_FAILED'} />
          </Toast>
        </ToastWrapper>
      )}

      {!cancelProduct && <ModalDescription>{translate('description')}</ModalDescription>}

      <ModalBody>
        {cancelProduct && (
          <CancelProductPart
            product={product}
            setProduct={(value) => setProduct && setProduct(value)}
            productOptions={products}
            error={formError && !product}
          />
        )}

        <Label error={formError && !form.reason}>{translate('select_title')}</Label>
        <RadioButtonGroup
          options={cancelReasons}
          onChange={(reason) => setForm({ ...form, reason })}
          selectedValue={form.reason}
        />

        <Label error={formError && !form.description}>{translate('textarea_title')}</Label>
        <StyledTextArea
          placeholder={translate('textarea_placeholder')}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
        <RequiredText error={formError && !form.description}>{translate('required')}</RequiredText>

        <ContactSupport>
          <Trans
            ns="accountSettings"
            i18nKey="plan_details.reason_modal.support_text"
            components={{
              1: <StyledLink onClick={openChat} style={styleReset} />,
              2: <BoldText />,
            }}
          />
        </ContactSupport>
        <ContactSupport>
          <Trans
            ns="accountSettings"
            i18nKey="plan_details.reason_modal.schedule_call_text"
            components={{
              1: (
                <StyledLink
                  href="https://calendly.com/cheq-essentials/overview-video-call"
                  target="_blank"
                  style={styleReset}
                />
              ),
              2: <BoldText />,
            }}
          />
        </ContactSupport>
      </ModalBody>
    </DesignedModal>
  )
}
